import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import LogoUpload from "../../components/common/logoUpload";
import { NPI } from "../../types/dashboard";
import { showToast } from "../../components/common/toastNotification";
import dashboardService from "../../services/DashboardServices";

interface NPIModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  NPIUpdateData?: NPI;
}

const NPIModal: React.FC<NPIModalProps> = ({
  open,
  onClose,
  onSave,
  NPIUpdateData,
}) => {
  useEffect(() => {
    if (NPIUpdateData) {
      setFormData({
        id: NPIUpdateData.id || null,
        title: NPIUpdateData.title || "",
        logoUrl: NPIUpdateData.logoUrl || "",
        description: NPIUpdateData.description || "",
        LogoPath: null,
        spaceLink: NPIUpdateData.spaceLink || "",
      });
    }
  }, [NPIUpdateData]);

  const [formData, setFormData] = useState<NPI>({
    id: null,
    title: "",
    description: "",
    logoUrl: "",
    spaceLink: "",
    LogoPath: null,
  });
  const [formErrors, setFormErrors] = useState<NPI>({
    title: "",
    spaceLink: "",
    logoUrl: "",
    description: "",
    LogoPath: null,
  });
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, LogoPath: file }));
    if (file) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        logoUrl: "",
      }));
    }
  };
  const validateForm = () => {
    const errors: Partial<NPI> = {};
    const urlRegex =
      /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

    if (!formData.title?.trim()) {
      errors.title = "Title is required.";
    }

    if (!formData.spaceLink?.trim()) {
      errors.spaceLink = "Google drive Link is required.";
    } else if (!urlRegex.test(formData.spaceLink.trim())) {
      errors.spaceLink = "Enter a valid URL for the SpaceLink.";
    }

    const logoUrl = formData.logoUrl?.trim() || "";
    if (!logoUrl && !formData.LogoPath) {
      errors.logoUrl = "Either a file or a URL must be provided.";
      showToast(errors.logoUrl, "error");
    } else if (logoUrl && !urlRegex.test(logoUrl)) {
      errors.logoUrl = "Enter a valid URL for the logo.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveData = async () => {
    if (!validateForm()) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("Id", formData.id?.toString() || "0");
      formDataToSend.append("Title", formData.title?.trim() || "");
      formDataToSend.append("Description", formData.description?.trim() || "");
      formDataToSend.append("SpaceLink", formData.spaceLink?.trim() || "");

      if (formData.LogoPath instanceof File) {
        formDataToSend.append("LogoPath", formData.LogoPath);
        formDataToSend.append("LogoUrl", "");
      } else if (formData.logoUrl?.trim()) {
        formDataToSend.append("LogoUrl", formData.logoUrl.trim());
        formDataToSend.append("LogoPath", "");
      }

      const apiCall = formData.id
        ? dashboardService.updateNPIData(formDataToSend)
        : dashboardService.addNPIData(formDataToSend);

      await apiCall
        .then((response) => {
          if (response && response.data) {
            showToast(response.data.message, "success");
            setFormData({
              id: 0,
              title: "",
              description: "",
              spaceLink: "",
              logoUrl: "",
              LogoPath: null,
            });
            onSave();
            onClose();
          } else {
            showToast(response?.data?.message || "Error occurred", "error");
          }
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      onSave={handleSaveData}
      title="NPI"
    >
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomInput
              label="Title"
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title || ""}
              onChange={handleChange}
              error={formErrors.title}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="Description"
              type="text"
              name="description"
              placeholder="Enter Description"
              value={formData.description || ""}
              rows={3}
              onChange={handleChange}
              error={formErrors.description}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LogoUpload
              label="Upload Logo"
              onChange={handleFileUpload}
              fileName={
                formData.LogoPath instanceof File ? formData.LogoPath.name : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <CustomInput
              label="Upload logo via the URL"
              type="text"
              name="logoUrl"
              placeholder="Enter URL"
              value={formData.logoUrl || ""}
              onChange={handleChange}
              error={formErrors.logoUrl}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="URL of the google drive Link"
              type="text"
              name="spaceLink"
              placeholder="Enter the URL of the google drive Link"
              value={formData.spaceLink || ""}
              onChange={handleChange}
              error={formErrors.spaceLink}
              required
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default NPIModal;
